import {
  get,
  getDownload,
  post,
  patch,
  postDownload,
  put,
  del,
} from '../utils/api';
import {
  buildGetUrlWithHudCompletedTab,
  getHudCompletedTabParamsFromOptions,
} from '../containers/GeneralAffordable/isHudCompletedTab';

import type { AffordableQualificationBackgroundAtAdmission } from '../types/backgroundAtAdmission';
import type { PublicHousingFamilySelfSufficiency } from '../types/familySelfSufficiency';

type RequestOptions = { [key: string]: mixed };

export default class AffordableQualificationService {
  getCompletionStatusesAndAssistanceDetails(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    const {
      isHudCompletedTab,
      affordableQualificationId: qualIdParam,
      fetchOptions,
    } = getHudCompletedTabParamsFromOptions(options);
    const url = buildGetUrlWithHudCompletedTab(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/completion-and-assistance-details`,
      {
        isHudCompletedTab,
        affordableQualificationId: qualIdParam ?? affordableQualificationId,
      },
    );
    // Completion state has to come from the server every time. On a history
    // navigation the browser defaults this GET to force-cache and replays the
    // stored response, ignoring `Cache-Control: no-cache` — so a household
    // completed seconds ago can still render its clocks red (FES-1097 root
    // caused the same bug for applicantFormService.js; this sibling read was
    // never covered by that fix).
    return get(url, { ...fetchOptions, cache: 'no-store' });
  }

  getCompletionStatuses(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    const {
      isHudCompletedTab,
      affordableQualificationId: qualIdParam,
      fetchOptions,
    } = getHudCompletedTabParamsFromOptions(options);
    const url = buildGetUrlWithHudCompletedTab(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/completion-statuses`,
      {
        isHudCompletedTab,
        affordableQualificationId: qualIdParam ?? affordableQualificationId,
      },
    );
    // Same history-navigation force-cache replay as
    // getCompletionStatusesAndAssistanceDetails above (FES-1316) — this read
    // backs the Public Housing completion clocks via
    // useAffordableQualificationCompletionStatusesQuery / usePublicHousing.js.
    return get(url, { ...fetchOptions, cache: 'no-store' });
  }

  calculateAssistanceDetails(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    programName: string,
    rentType?: string,
    options?: Object,
    force: boolean = false,
  ) {
    const payload = {
      force,
      affordableQualificationId,
      programName,
      rentType,
    };

    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-assistance-details/calculate`,
      JSON.stringify(payload),
      options,
    );
  }

  overrideNeedsReview(
    organizationId: string,
    propertyId: string,
    needsReviewId: string,
    reason?: string,
    options?: Object,
  ) {
    const payload = {
      needsReviewId,
      reason,
    };

    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualification-needs-reviews/override`,
      JSON.stringify(payload),
      options,
    );
  }

  startRecertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds: Array<string>,
    voucherEffectiveDate: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return put(
      `/${orgProp}/households/${householdId}/start-recert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
        client: 'management-dashboard',
      }),
      options,
    );
  }

  startInitialCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds?: Array<string>,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-initial-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startCoTenantToTenantCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds?: Array<string>,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-co-tenant-to-tenant-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startTerminationCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds?: Array<string>,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-termination-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startTerminateAbsenceCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds?: Array<string>,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-terminate-absence-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startMoveOutCertification(
    organizationId: string,
    propertyId: string,
    residentId: string,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    const payload = voucherEffectiveDate ? { voucherEffectiveDate } : {};
    return post(
      `/${orgProp}/residents/${residentId}/start-move-out-cert`,
      JSON.stringify(payload),
      options,
    );
  }

  startInitiateAbsenceCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds?: Array<string>,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-initiate-absence-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startEvictionCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds?: Array<string>,
    voucherEffectiveDate?: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-eviction-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startInterimCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds: Array<string>,
    voucherEffectiveDate: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-interim-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startCorrectionCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/start-correction-cert`,
      null,
      options,
    );
  }

  startAssignRemoveRACertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/start-assign-remove-ra-cert`,
      null,
      options,
    );
  }

  startUnitTransferCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds: Array<string>,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-unit-transfer-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
      }),
      options,
    );
  }

  startFlatRentAnnualUpdateCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds: Array<string>,
    voucherEffectiveDate: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-flat-rent-annual-update-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  startFssAddendumCertification(
    organizationId: string,
    propertyId: string,
    householdId: string,
    propertyAffordableProgramId: string,
    applicantIds: Array<string>,
    voucherEffectiveDate: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/start-fss-addendum-cert`,
      JSON.stringify({
        propertyAffordableProgramId,
        applicantIds,
        voucherEffectiveDate,
      }),
      options,
    );
  }

  reOpenCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/reopen-cert`,
      null,
      options,
    );
  }

  voidCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/void-cert`,
      null,
      options,
    );
  }

  removeCorrection(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: RequestOptions,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/remove-correction`,
      null,
      options,
    );
  }

  completeCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    isReComplete: ?boolean,
    useInterimEffectiveDateForDueDate?: boolean,
    ownerSignatureDate?: string | null,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    const body = {
      isReComplete,
      useInterimEffectiveDateForDueDate,
      ownerSignatureDate,
    };
    return post(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/complete-certification`,
      JSON.stringify(body),
      options,
    );
  }

  refreshCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return put(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/refresh-gross-rent`,
      null,
      options,
    );
  }

  saveAffordableQualification(
    payload: Object,
    organizationId: string,
    propertyId: string,
    householdId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/households/${householdId}/affordable-qualifications/${affordableQualificationId}`,
      JSON.stringify(payload),
      options,
    );
  }

  saveQualificationChecklistApplicant(
    checklist: Object,
    affordableQualificationId: any,
    organizationId: string,
    propertyId: string,
    applicantId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    const applicationBaseUrl = `${orgProp}/affordable-qualifications/${affordableQualificationId}`;

    return put(
      `/${applicationBaseUrl}/applicants/${applicantId}/affordable-qualification-checklist`,
      JSON.stringify(checklist),
      options,
    );
  }

  getRecertForm(
    organizationId: string,
    propertyId: string,
    applicantId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    const {
      isHudCompletedTab,
      affordableQualificationId: qualIdParam,
      fetchOptions,
    } = getHudCompletedTabParamsFromOptions(options);
    const url = buildGetUrlWithHudCompletedTab(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/applicant/${applicantId}/recert-form`,
      {
        isHudCompletedTab,
        affordableQualificationId: qualIdParam ?? affordableQualificationId,
      },
    );
    return get(url, fetchOptions);
  }

  saveRecertForm(
    formData: Object,
    organizationId: string,
    propertyId: string,
    applicantId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return put(
      `/${orgProp}/affordable-qualifications/${affordableQualificationId}/applicants/${applicantId}/recert-form`,
      JSON.stringify(formData),
      options,
    );
  }

  saveCorrection(
    formData: Object,
    organizationId: string,
    propertyId: string,
    options?: Object,
  ) {
    const orgProp = `${organizationId}/${propertyId}`;
    return post(
      `/${orgProp}/affordable-qualification-corrections`,
      JSON.stringify(formData),
      options,
    );
  }

  generate59(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    fileName: string,
    options?: Object,
  ) {
    return postDownload(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/generate59`,
      fileName,
      JSON.stringify({ fileName }),
      options,
    );
  }

  generate59WithNotices(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    fileName: string,
    includeVawa: boolean = false,
    options?: Object,
  ) {
    const body = {
      fileName,
      includeVawa,
    };

    return postDownload(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/generate59WithNotices`,
      fileName,
      JSON.stringify(body),
      options,
    );
  }

  generate50058(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    fileName: string,
    options?: Object,
  ) {
    return postDownload(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/generate58`,
      fileName,
      JSON.stringify({ fileName }),
      { ...options, fullError: true },
    );
  }

  generateAndDownloadAll59As(
    organizationId: string,
    propertyId: string,
    fileName: string,
    options?: Object,
    programName?: string = 'HUD',
  ) {
    return postDownload(
      `/${organizationId}/${propertyId}/generate-all-gross-rent-59a`,
      fileName,
      JSON.stringify({ programName }),
      options,
    );
  }

  batchGenerateGrossRent59As(
    organizationId: string,
    propertyId: string,
    fileName: string,
    options?: Object,
    grEffectiveDate?: ?string,
  ) {
    return postDownload(
      `/${organizationId}/${propertyId}/batch-generate-gross-rent-59a`,
      fileName,
      JSON.stringify({ fileName, grEffectiveDate }),
      options,
    );
  }

  generateRD(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    fileName: string,
    options?: Object,
  ) {
    return postDownload(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/generateRD`,
      fileName,
      JSON.stringify({ fileName }),
      options,
    );
  }

  /**
   * Metadata for the document saved on a completed certification -- its `certDocDataId` pin, or the
   * newest generated document when there is no pin. Pure read: never generates or recalculates, so
   * it is safe to call on every Completed tab render.
   */
  getPinnedCertificationDocument(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    return get(
      // eslint-disable-next-line max-len
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/pinned-certification-document`,
      options,
    );
  }

  /**
   * Generates the final certification document for a completed certification and pins it. This
   * writes: it builds a PDF, uploads it, and sets the pin. Call it only when
   * getPinnedCertificationDocument reported canGenerate.
   */
  generateFinalCertificationDocument(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    return post(
      // eslint-disable-next-line max-len
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/final-certification-document`,
      null,
      options,
    );
  }

  cancelCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    return del(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/cancel`,
      undefined,
      options,
    );
  }

  convertCertification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    certificationType: string,
    options?: Object,
  ) {
    return post(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/convert`,
      JSON.stringify({ certificationType }),
      options,
    );
  }

  downloadRecertificationForm(
    organizationId: string,
    propertyId: string,
    applicantId: string,
    affordableQualificationId: string,
    fileName: string,
    options?: Object,
  ) {
    const params = `?applicantId=${applicantId}`;
    return getDownload(
      // eslint-disable-next-line max-len
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/download-recert-form${params}`,
      fileName,
      { options },
    );
  }

  downloadIncomeAssetForm(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    applicantId: string,
    type: string,
    fileName: string,
    programName?: ?string,
    options?: Object,
  ) {
    const programParam =
      programName != null && String(programName).length > 0
        ? `&programName=${encodeURIComponent(String(programName))}`
        : '';
    const params = `?type=${type}&applicantId=${applicantId}${programParam}`;
    return getDownload(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/download-form${params}`,
      fileName,
      { options },
    );
  }

  downloadDefaultIncomeAssetForm(
    organizationId: string,
    applicationId: string,
    applicantId: string,
    propertyId: string,
    type: string,
    fileName: string,
    options?: Object,
  ) {
    const params = `?type=${type}&applicationId=${applicationId}&applicantId=${applicantId}`;
    return getDownload(
      `/${organizationId}/${propertyId}/affordable-qualifications/download-default-form${params}`,
      fileName,
      { options },
    );
  }

  getAffordableQualificationDocuments(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: Object,
  ) {
    const {
      isHudCompletedTab,
      affordableQualificationId: qualIdParam,
      fetchOptions,
    } = getHudCompletedTabParamsFromOptions(options);
    const url = buildGetUrlWithHudCompletedTab(
      // eslint-disable-next-line max-len
      `/${organizationId}/${propertyId}/affordable-qualification/${affordableQualificationId}/affordable-qualification-document`,
      {
        isHudCompletedTab,
        affordableQualificationId: qualIdParam ?? affordableQualificationId,
      },
    );
    return get(url, fetchOptions);
  }

  async patchAffordableQualification(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    valuesToModify: Object,
    isEdited: boolean = false,
    options?: Object,
  ) {
    return patch(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}?isEdited=${isEdited}`,
      JSON.stringify(valuesToModify),
      options,
    );
  }

  async getById(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    options?: object,
  ): Promise<AffordableQualification> {
    return get(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}`,
      options,
    );
  }

  async putAffordableQualificationBackgroundAtAdmission(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    payload: Object,
    options?: Object,
  ): Promise<AffordableQualificationBackgroundAtAdmission> {
    return put(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/background-at-admission`,
      JSON.stringify(payload),
      options,
    );
  }

  async putFamilySelfSufficiency(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    payload: Object,
    options?: Object,
  ): Promise<PublicHousingFamilySelfSufficiency> {
    return put(
      `/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/family-self-sufficiency`,
      JSON.stringify(payload),
      options,
    );
  }
}
